import {useEffect} from "react";
import {BrowserRouter, Routes, Route} from "react-router";
import {QueryClientProvider} from "@tanstack/react-query";
import {queryClient} from "@/lib/queryClient.js";
import {getToken} from "@/utils/authToken.js";
import {useAppConfig} from "@/hooks/useAppConfig.js";
import {useAirports} from "@/hooks/useAirports.js";
import {useSelectables} from "@/hooks/useSelectables.js";
import {AuthProvider, useAuth} from "@/context/AuthContext.jsx";
import {SidebarProvider} from "@/context/SidebarContext.jsx";
import ProtectedRoute from "@/components/ProtectedRoute.jsx";
import RefAuthGate from "@/components/RefAuthGate.jsx";
import PhoneBookingRouteGate from "@/components/PhoneBookingRouteGate.jsx";
import AppLayout from "@/layouts/AppLayout.jsx";
import Home from "@/layouts/Home.jsx";
import SignIn from "@/layouts/auth/SignIn.jsx";
import NotFound from "@/layouts/NotFound.jsx";
import Bookings from "@/layouts/Bookings.jsx";
import OngoingRides from "@/layouts/rides/OngoingRides.jsx";
import UpcomingRides from "@/layouts/rides/UpcomingRides.jsx";
import CompletedRides from "@/layouts/rides/CompletedRides.jsx";
import CancelledRides from "@/layouts/rides/CancelledRides.jsx";
import AllRides from "@/layouts/rides/AllRides.jsx";
import RideDetails from "@/layouts/RideView/RideDetails.jsx";
import Payment from "@/layouts/Payment.jsx";
import Promotions from "@/layouts/Promotions.jsx";
import Notifications from "@/layouts/Notifications.jsx";
import Help from "@/layouts/Help.jsx";
import EditProfile from "@/layouts/EditProfile.jsx";
import SignUp from "@/layouts/auth/SignUp.jsx";
import OtpVerification from "@/layouts/auth/OtpVerification.jsx";
import Authorize from "@/layouts/auth/Authorize.jsx";
import DeleteAccountPage from "@/layouts/DeleteAccountPage.jsx";
import RideEdit from "@/layouts/RideEdit.jsx";
import RideConfirm from "@/layouts/RideConfirm.jsx";
import PhoneBookingConfirmation from "@/layouts/PhoneBookingConfirmation.jsx";
import RideTripView from "@/layouts/RideTripView.jsx";
import ReturnBooking from "@/layouts/rides/ReturnBooking.jsx";
import Rebook from "@/layouts/rides/Rebook.jsx";
import BookRide from "@/layouts/BookRide.jsx";
import TrackDriver from "@/layouts/TrackDriver.jsx";
import PreciseLocationNotice from "@/components/PreciseLocationNotice.jsx";
import {isDebug} from '@/utils/debug'

/**
 * Loads the app config (/config/app) into the react-query cache on mount so
 * BOOKING_CONSTRAINTS can read it. Also fetches the booking selectables here —
 * anchored at the root so they survive a reload into any route (e.g. straight
 * into DestinationBanner), instead of relying on MainBanner mounting. Renders
 * nothing.
 */
// Trip surfaces take their passenger / child-seat / luggage types from the trip response's
// own `selectables` (see useEditTrip's hydration), so the booking selectables are three dead
// requests there. /return-booking and /rebook are included because they only exist to redirect into
// /rides/:id/confirm. Read off window.location: this component sits OUTSIDE the router, and
// the prefetch is a mount-time decision — the lists are static and session-cached, and any
// booking page that needs them calls its own use*Types() hook anyway.
const TRIP_ONLY_PATHS = ['/rides', '/return-booking', '/rebook'];

// ⚠️ MATCHED ON THE SUFFIX, because the trip id is the FIRST segment on this one
// (`/{tripId}/track-driver`) and there is no prefix to test. Tracking reads a trip that
// already exists — it books nothing — so the three selectables requests above are as dead
// here as they are on /rides.
const TRIP_ONLY_SUFFIXES = ['/track-driver'];

function AppConfigLoader() {
    // /config/app is fetched SIGNED OUT TOO, unlike everything below it: the auth screens
    // read `social_auth.*` from it for the Google/Apple buttons, and the footer/support
    // surfaces read `general.*`. Gating it would break sign-in itself.
    useAppConfig();

    // Everything below is reference data for BOOKING, and a signed-out visitor is on
    // /signin, /signup or /otp-verification, where none of it is read. Gating on the
    // session keeps four requests off the auth screens; they run on the next render after
    // login (AuthContext re-renders this component), and on every reload while signed in,
    // which is when the cache is empty again.
    //
    // Every "public" route still ends up authenticated before its page renders —
    // /authorize calls login(), PhoneBookingRouteGate only renders children once
    // `isAuthenticated` — so nothing that needs these lists is left without them. The
    // surfaces that read them (hopnModal's boot gate, useAirportDetector) call the same
    // hooks themselves, so this is only a warm-up, never the sole fetch.
    const {isAuthenticated} = useAuth();

    // /config/airports backs the airport DETECTION (utils/airports.js), which is
    // synchronous over the react-query cache and reports "not an airport" until the list
    // lands. NOT gated by path — every signed-in surface derives a trip direction from
    // it: booking, /rides/:id/edit and /rides/:id/confirm alike.
    useAirports({enabled: isAuthenticated});
    useSelectables({
        enabled: isAuthenticated
            && !TRIP_ONLY_PATHS.some((p) => window.location.pathname.startsWith(p))
            && !TRIP_ONLY_SUFFIXES.some((p) => window.location.pathname.endsWith(p)),
    });
    // Debug: print the current access token to the console so it can be grabbed easily.
    useEffect(() => {
        if (isDebug('hopnDebug')) console.log("Access Token:", getToken());
    }, []);
    return null;
}

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <SidebarProvider>
                <AuthProvider>
                    <AppConfigLoader/>
                    {/* Mobile only: asks to switch "Approximate" location to precise. */}
                    <PreciseLocationNotice/>
                    <BrowserRouter>
                        <RefAuthGate>
                        <Routes>
                            {/* Public routes */}
                            <Route path="/signin" element={<SignIn/>}/>
                            <Route path="/signup" element={<SignUp/>}/>
                            <Route path="/otp-verification" element={<OtpVerification/>}/>
                            {/* Handoff links land here when the ref has to be proved — see
                                RefAuthGate. Public on purpose: the passenger has no session yet. */}
                            <Route path="/authorize" element={<Authorize/>}/>

                            {/* ⚠️ PUBLIC, AND IT HAS TO BE. Google Play requires a way to
                                delete an account WITHOUT the app installed, so this cannot sit
                                behind ProtectedRoute and must not redirect to sign-in — a
                                person who has lost their phone has to be able to finish here.
                                The URL is registered in Play Console; renaming it is a store
                                submission, not a refactor. See `DeleteAccountPage`. */}
                            <Route path="/delete-account" element={<DeleteAccountPage/>}/>

                            {/* The phone desk's hand-off — PUBLIC, and the only page here
                                that is. DELIBERATELY UNLINKED: nothing in the dashboard
                                navigates to it, the URL an agent reads out is the only way
                                in. It keeps AppLayout (which is what honours
                                `?layout=minimal`) but NOT ProtectedRoute: the passenger
                                arrives from an SMS with no session and often no account
                                they know about, so /signin is a dead end for them. The page
                                proves them by OTP instead — PhoneBookingRouteGate.
                                Drafts only: the page itself refuses any other status. */}
                            <Route element={<AppLayout/>}>
                                <Route
                                    path="/rides/:rideId/phone-booking-confirmation"
                                    element={
                                        <PhoneBookingRouteGate>
                                            <PhoneBookingConfirmation/>
                                        </PhoneBookingRouteGate>
                                    }
                                />
                            </Route>

                            {/* Protected routes */}
                            <Route element={
                                <ProtectedRoute>
                                    <AppLayout/>
                                </ProtectedRoute>
                            }>
                                <Route path="/" element={<Home/>}/>
                                <Route path="/rides" element={<Bookings/>}/>
                                <Route path="/rides/all" element={<AllRides/>}/>
                                <Route path="/rides/ongoing" element={<OngoingRides/>}/>
                                <Route path="/rides/upcoming" element={<UpcomingRides/>}/>
                                <Route path="/rides/completed" element={<CompletedRides/>}/>
                                <Route path="/rides/cancelled" element={<CancelledRides/>}/>
                                <Route path="/rides/:rideId" element={<RideDetails/>}/>
                                <Route path="/rides/:rideId/edit" element={<RideEdit/>}/>
                                <Route path="/rides/:rideId/confirm" element={<RideConfirm/>}/>
                                {/* /rides/:rideId/phone-booking-confirmation is NOT here —
                                    it is public, above. */}
                                {/* Read-only twin of /edit — the trip-info bar's right-arrow lands here. */}
                                <Route path="/rides/:rideId/view" element={<RideTripView/>}/>
                                <Route path="/return-booking/:parentTripId" element={<ReturnBooking/>}/>
                                {/* Link-reachable rebook (emailed "Rebook" button) — see Rebook.jsx. */}
                                <Route path="/rebook/:tripId" element={<Rebook/>}/>
                                <Route path="/book-ride" element={<BookRide/>}/>
                                <Route path="/payment" element={<Payment/>}/>
                                <Route path="/promotions" element={<Promotions/>}/>
                                <Route path="/notifications" element={<Notifications/>}/>
                                <Route path="/help" element={<Help/>}/>
                                <Route path="/settings" element={<EditProfile/>}/>


                                {/* LIVE DRIVER TRACKING — top level on purpose, because the
                                    link is emailed and read on a phone.

                                    ⚠️ PROTECTED, AND THAT IS WHAT MAKES THE EMAIL LINK WORK
                                    rather than what breaks it. `RefAuthGate` wraps every
                                    route, so a recipient arriving on `?ref=…` with no
                                    session is proved and returned here. Public, any trip id
                                    would show a stranger's car moving in real time.

                                    ⚠️ NO COLLISION with `/rides/:rideId`,
                                    `/rebook/:tripId` or `/return-booking/:parentTripId`.
                                    React Router ranks a static segment above a dynamic one,
                                    so those win their own first segment outright; the only
                                    URLs that could tie are `/rides/track-driver` and the
                                    like, which are not routes and cannot be trip ids. */}
                                <Route path="/:tripId/track-driver" element={<TrackDriver/>}/>

                                {/* ── ⚠️ THE ARRIVAL GUIDE HAS NO ROUTE AT ALL (2026-09-23)
                                    `/:tripId/welcome` stood here and is GONE — not merely
                                    unlinked. The guide is a modal raised from `AppLayout`:
                                    it opens over whatever page the passenger chose, closes
                                    back to it, and the plane button bottom-right reopens
                                    it. Keeping the route "for deep links" left the welcome
                                    reachable as a PAGE — a second screen owning a landed
                                    airport ride, which is the exact thing the modal was
                                    made to end.

                                    `ArrivalGuideRoute` was this route's gate and went with
                                    it; the same questions (is it from-airport, has the ride
                                    started) are asked where the modal is raised. The walk
                                    and the written directions were never routes — they are
                                    sheets inside `FromAirportArrived`, which `WelcomeModal`
                                    is now the only thing that renders. */}
                            </Route>

                            {/* 404 catch-all */}
                            <Route path="*" element={<NotFound/>}/>
                        </Routes>
                        </RefAuthGate>
                    </BrowserRouter>
                </AuthProvider>
            </SidebarProvider>
        </QueryClientProvider>
    )
}

export default App
